import {
  Button,
  Chip,
  SettingsCard,
  SettingsCardActions,
  SettingsCardButton,
  SettingsCardSkeleton,
  twoStepKindsToSetUp,
  Typography,
  type SettingsCardStatus,
  type TwoStepDevices,
  type TwoStepMethodKind,
  type TwoStepStatus,
} from '@idu-nightlife/ui-kit';
import { Divider, Stack } from '@mui/material';
import { format } from 'date-fns';
import type { ReactNode } from 'react';
import type { AccountSecurityActions, RememberedDevicesLoad } from './account-security.types';

const STATE_STATUS: Record<TwoStepStatus['twoStep'], SettingsCardStatus> = {
  off: { label: 'Off', color: 'default' },
  on: { label: 'On', color: 'success' },
  required: { label: 'Required', color: 'success' },
};

const METHOD_LABELS: Record<TwoStepMethodKind, string> = {
  authenticator: 'Authenticator App',
  sms: 'Text Message',
};

const Reason = ({ children }: { children: ReactNode }) => (
  <Typography.Caption>{children}</Typography.Caption>
);

type SectionProps = { status: TwoStepStatus; busy: boolean };

export const TwoStepStateSection = ({
  status,
  busy,
  onTurnOn,
  onTurnOff,
}: SectionProps & Pick<AccountSecurityActions, 'onTurnOn' | 'onTurnOff'>) => {
  const hasMethod = status.methods.length > 0;

  return (
    <SettingsCard
      title="Two-Step Sign-In"
      subheader="Asks for a code after your password, so a password alone can't sign you in."
      status={STATE_STATUS[status.twoStep]}
    >
      {status.twoStep === 'required' && (
        <Typography.Body>
          Required for your iDU Nightlife access, so it can&apos;t be turned off.
        </Typography.Body>
      )}
      {status.twoStep === 'off' && (
        <>
          {!hasMethod && <Reason>Add a way to sign in first.</Reason>}
          <SettingsCardActions>
            <SettingsCardButton variant="primary" disabled={busy || !hasMethod} onClick={onTurnOn}>
              Turn On
            </SettingsCardButton>
          </SettingsCardActions>
        </>
      )}
      {status.twoStep === 'on' && (
        <SettingsCardActions>
          <SettingsCardButton variant="secondary" color="error" disabled={busy} onClick={onTurnOff}>
            Turn Off
          </SettingsCardButton>
        </SettingsCardActions>
      )}
    </SettingsCard>
  );
};

export const MethodsSection = ({
  status,
  busy,
  onMakePreferred,
  onRemove,
  onAddMethod,
  onChangeNumber,
}: SectionProps &
  Pick<
    AccountSecurityActions,
    'onMakePreferred' | 'onRemove' | 'onAddMethod' | 'onChangeNumber'
  >) => {
  const isLastNeeded = status.methods.length === 1 && status.twoStep !== 'off';
  const canAdd = twoStepKindsToSetUp(status).length > 0;

  return (
    <SettingsCard
      title="Ways to Sign In"
      subheader="How you get your sign-in codes. Your preferred way is asked for first."
    >
      {status.methods.length === 0 && (
        <Typography.Subtitle>No ways to sign in are set up yet.</Typography.Subtitle>
      )}
      {status.methods.length > 0 && (
        <Stack divider={<Divider />} sx={{ gap: 1 }}>
          {status.methods.map(({ kind, preferred, hint }) => (
            <Stack key={kind} sx={{ gap: 0.5 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                sx={{ gap: 1, justifyContent: 'space-between', alignItems: { md: 'center' } }}
              >
                <Stack direction="row" sx={{ gap: 1, alignItems: 'center', minWidth: 0 }}>
                  <Typography.BodyEmphasised>{METHOD_LABELS[kind]}</Typography.BodyEmphasised>
                  {hint && <Typography.Subtitle noWrap>{hint}</Typography.Subtitle>}
                  {preferred && <Chip label="Preferred" variant="muted" color="primary" />}
                </Stack>
                <Stack direction="row" sx={{ gap: 1 }}>
                  {!preferred && (
                    <Button variant="text" disabled={busy} onClick={() => onMakePreferred(kind)}>
                      Make Preferred
                    </Button>
                  )}
                  {kind === 'sms' && (
                    <Button
                      variant="text"
                      disabled={busy || !status.smsAvailable}
                      onClick={onChangeNumber}
                    >
                      Change
                    </Button>
                  )}
                  <Button
                    variant="text"
                    color="error"
                    disabled={busy || isLastNeeded}
                    onClick={() => onRemove(kind)}
                  >
                    Remove
                  </Button>
                </Stack>
              </Stack>
              {isLastNeeded && (
                <Reason>Add another way to sign in before removing this one.</Reason>
              )}
            </Stack>
          ))}
        </Stack>
      )}
      {!canAdd && <Reason>Every available way to sign in is set up.</Reason>}
      <SettingsCardActions>
        <SettingsCardButton variant="secondary" disabled={busy || !canAdd} onClick={onAddMethod}>
          Add a Method
        </SettingsCardButton>
      </SettingsCardActions>
    </SettingsCard>
  );
};

const shortDate = (iso: string) => format(new Date(iso), 'd MMM yyyy');

const deviceHistory = (device: TwoStepDevices[number]) =>
  [
    `Remembered ${shortDate(device.rememberedAt)}`,
    device.lastUsedAt ? `last used ${shortDate(device.lastUsedAt)}` : 'not used since',
    `asks again from ${shortDate(device.expiresAt)}`,
  ].join(' · ');

export const RememberedDevicesSection = ({
  devices,
  busy,
  onRetryDevices,
  onForgetDevice,
  onForgetAllDevices,
}: { devices: RememberedDevicesLoad; busy: boolean } & Pick<
  AccountSecurityActions,
  'onRetryDevices' | 'onForgetDevice' | 'onForgetAllDevices'
>) => {
  if (devices.state === 'loading') return <SettingsCardSkeleton />;

  const remembered = devices.state === 'ready' ? devices.devices : [];

  return (
    <SettingsCard
      title="Remembered Devices"
      subheader="Devices where you chose not to be asked for a code. Forget one to be asked again there."
    >
      {devices.state === 'error' && (
        <Typography.Subtitle>Your remembered devices couldn&apos;t be loaded.</Typography.Subtitle>
      )}
      {devices.state === 'ready' && remembered.length === 0 && (
        <Typography.Subtitle>No devices are remembered.</Typography.Subtitle>
      )}
      {remembered.length > 0 && (
        <Stack divider={<Divider />} sx={{ gap: 1 }}>
          {remembered.map((device) => (
            <Stack
              key={device.id}
              direction={{ xs: 'column', md: 'row' }}
              sx={{ gap: 1, justifyContent: 'space-between', alignItems: { md: 'center' } }}
            >
              <Stack sx={{ minWidth: 0 }}>
                <Typography.BodyEmphasised>
                  {device.label ?? 'Unnamed device'}
                </Typography.BodyEmphasised>
                <Typography.Caption>{deviceHistory(device)}</Typography.Caption>
              </Stack>
              <Button
                variant="text"
                color="error"
                disabled={busy}
                onClick={() => onForgetDevice(device.id)}
              >
                Forget
              </Button>
            </Stack>
          ))}
        </Stack>
      )}
      <SettingsCardActions>
        {devices.state === 'error' ? (
          <SettingsCardButton variant="secondary" onClick={onRetryDevices}>
            Try Again
          </SettingsCardButton>
        ) : (
          <SettingsCardButton
            variant="secondary"
            color="error"
            disabled={busy || remembered.length === 0}
            onClick={onForgetAllDevices}
          >
            Forget All
          </SettingsCardButton>
        )}
      </SettingsCardActions>
    </SettingsCard>
  );
};
